import React from 'react';
import { Icon, Table } from '@hvcgroep/design-system';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import { api, InvoiceResponseModel } from '../../store/api-generated';
import formatCurrency from '../../helpers/formatCurrency';

import './style.scss';
import StatusIcon from '../StatusIcon';
import useDownloadDocument from '../../hooks/useDownloadDocument';

interface Props {
  invoices: InvoiceResponseModel[]
  className?: string;
}

function formatDate(d: string) {
  const date = new Date(d);

  return new Intl.DateTimeFormat(document.documentElement.lang, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

const InvoiceTable: React.FC<Props> = ({
  invoices,
  className,
}) => {
  const { t } = useTranslation();
  const headers: (keyof Omit<InvoiceResponseModel, 'accountId' | 'dueDate'>)[] = ['id', 'creationDate', 'totalAmount', 'dueAmount', 'status'];

  const classes = c('mo-invoice-table', className);

  const { downloadDocument } = useDownloadDocument();

  return (
    <Table className={classes}>
      <Table.Head>
        <Table.Row>
          {headers.map((key) => {
            return (
              <Table.Header scope="col" key={`table-header-${key}`} numeric={key === 'totalAmount' || key === 'dueAmount'}>
                {t(`invoiceTable.table.headers.${key}`)}
              </Table.Header>
            );
          })}
        </Table.Row>
      </Table.Head>

      <Table.Body>
        {invoices.map((invoice) => {
          return (
            <Table.Row key={String(invoice.id)}>
              <Table.Header scope="row">
                <button
                  className="mo-invoice-table__invoice-id"
                  onClick={() => {
                    if (invoice.id) {
                      downloadDocument({
                        documentId: invoice.id,
                        documentType: 'invoice',
                      });
                    }
                  }}
                  type="button"
                >
                  <Icon icon="download" />
                  {invoice.id}
                </button>
              </Table.Header>
              <Table.Data>
                {invoice.creationDate && formatDate(invoice.creationDate)}
              </Table.Data>
              <Table.Data numeric className="mo-invoice-table__total-amount">
                {invoice.totalAmount && formatCurrency(invoice.totalAmount)}
              </Table.Data>
              <Table.Data numeric>
                {invoice.dueAmount != null && formatCurrency(invoice.dueAmount)}
              </Table.Data>
              <Table.Data className="mo-invoice-table__status">
                {invoice?.status?.toLowerCase() === 'paid' && (
                  <StatusIcon status="success" textPosition="after" text={t(`invoiceTable.table.status.${invoice?.status?.toLowerCase() as 'paid'}`)} />
                )}
                {invoice?.status?.toLowerCase() === 'open' && (
                  <StatusIcon status="pending" textPosition="after" text={t(`invoiceTable.table.status.${invoice?.status?.toLowerCase() as 'paid'}`)} />
                )}
              </Table.Data>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};

export default InvoiceTable;
